import React from "react";
import { Box, Typography, Divider, Alert } from "@mui/material";
import AppModal from "../common/AppModal";
import DetailItem from "../common/DetailItem";
import { formatCurrency } from "../../utils/currencyUtils";
import AppCircularProgress from "../common/AppCircularProgress";
import { CustomerResponseDto } from "../../shared/dtos/customer/CustomerResponseDto";

interface ViewCustomerModalProps {
  open: boolean;
  onClose: () => void;
  customer: CustomerResponseDto | null;
  loading: boolean;
  error: string | null;
}

const ViewCustomerModal: React.FC<ViewCustomerModalProps> = ({
  open,
  onClose,
  customer,
  loading,
  error,
}) => {
  const renderContent = (): React.ReactNode => {
    if (loading) return <AppCircularProgress />;
    if (error)
      return (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      );
    if (!customer) return null;

    return (
      <Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <DetailItem label="Full Name / Entity" value={customer.name} />
          <DetailItem label="PIN / Tax ID" value={customer.taxId || "N/A"} />
          <DetailItem label="Primary Phone" value={customer.primaryContact} />
          <DetailItem
            label="Secondary Phone"
            value={customer.secondaryContact || "N/A"}
          />
          <DetailItem label="Email Address" value={customer.email || "N/A"} />
          <DetailItem
            label="Status"
            value={customer.isActive ? "Active Account" : "Deactivated"}
          />
        </Box>

        <Divider sx={{ my: 2 }}>
          <Typography variant="overline" sx={{ fontWeight: 700 }}>
            Financials
          </Typography>
        </Divider>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <DetailItem
            label="Approved Credit Limit"
            value={formatCurrency(customer.creditLimit)}
          />
          <DetailItem
            label="Accumulated Loyalty Points"
            value={`${customer.loyaltyPoints} Points`}
          />
        </Box>

        <Divider sx={{ my: 2 }}>
          <Typography variant="overline" sx={{ fontWeight: 700 }}>
            Address Details
          </Typography>
        </Divider>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <DetailItem
            label="Street"
            value={`${customer.address.streetLine1} ${customer.address.streetLine2 || ""}`}
          />
          <DetailItem label="City" value={customer.address.city} />
          <DetailItem
            label="Province/State"
            value={customer.address.stateOrProvince}
          />
          <DetailItem label="Postal Code" value={customer.address.postalCode} />
          <DetailItem label="Country" value={customer.address.country} />
        </Box>
      </Box>
    );
  };

  return (
    <AppModal open={open} onClose={onClose} title="Customer Profile" width="md">
      {renderContent()}
    </AppModal>
  );
};

export default ViewCustomerModal;
